import { useState } from "react";
import { motion } from "framer-motion";
import { Plus } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

export const FAQS = [
  {
    id: "ag-visio",
    q: "Comment se déroule une AG en visioconférence ?",
    a: "L'AG peut être tenue à distance ou en format hybride, présentiel et distanciel. La feuille de présence est digitalisée, le vote se fait en ligne de façon sécurisée et le procès-verbal est généré automatiquement.",
  },
  {
    id: "lre",
    q: "Qu'est-ce que la LRE ?",
    a: "La Lettre Recommandée Électronique est l'équivalent juridique d'un recommandé papier. Syndicappli l'utilise pour les convocations d'AG et les procès-verbaux.",
  },
  {
    id: "vote-securise",
    q: "Le vote électronique est-il sécurisé ?",
    a: "Oui : vote électronique à distance sécurisé et vote par correspondance digitalisé, tracés dans l'application.",
  },
  {
    id: "incidents",
    q: "Comment déclarer un incident ?",
    a: "Depuis l'application, en quelques clics, avec photo ou vidéo à l'appui. Le suivi continue jusqu'à la résolution, avec des notifications à chaque étape.",
  },
  {
    id: "supports",
    q: "Sur quels supports fonctionne Syndicappli ?",
    a: "Sur smartphone, PC, Mac et tablette : application mobile gratuite iOS et Android, et webapp sur toute plateforme.",
  },
  {
    id: "support",
    q: "Le support est-il disponible en dehors des heures ouvrées ?",
    a: "Oui : assistance en ligne par chatbot 24h/24 et 7j/7, et support client avec numéro dédié.",
  },
  {
    id: "tarifs",
    q: "Combien coûte Syndicappli ?",
    a: "9€ HT par lot et par mois pour les copropriétaires, 8€ HT par lot et par mois pour les syndics. Le module Comptabilité est disponible en option, sur devis.",
  },
  {
    id: "demo",
    q: "Comment réserver une démonstration ?",
    a: "Réservez un créneau de 30 minutes directement sur notre Calendly, ou écrivez à contact@syndicappli.fr.",
  },
];

export const Faq = () => {
  const [open, setOpen] = useState("ag-visio");

  return (
    <section id="faq" className="bg-mist py-24 lg:py-32" data-testid="faq-section">
      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="max-w-3xl"
        >
          <span className="inline-flex items-center gap-3 text-xs font-bold uppercase tracking-[0.25em] text-electric">
            <span className="h-px w-10 bg-electric" />
            FAQ
          </span>
          <h2 className="mt-6 text-4xl font-bold tracking-normal leading-[1.15] text-ink md:text-5xl">
            Questions{" "}
            <span className="bg-[linear-gradient(100deg,#00C6CC,#2FCB74)] bg-clip-text text-transparent">
              fréquentes.
            </span>
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-4 md:grid-cols-2">
          {FAQS.map((f, i) => {
            const isOpen = open === f.id;
            return (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: (i % 2) * 0.08, ease: EASE }}
                className={`h-fit rounded-3xl border bg-white p-7 transition-shadow duration-300 ${
                  isOpen
                    ? "border-electric/40 shadow-[0_20px_50px_-25px_rgba(0,190,200,0.5)]"
                    : "border-black/5 shadow-[0_15px_40px_-25px_rgba(45,52,54,0.3)]"
                }`}
                data-testid={`faq-item-${f.id}`}
              >
                <button
                  onClick={() => setOpen(isOpen ? null : f.id)}
                  aria-expanded={isOpen}
                  className="flex w-full items-start justify-between gap-4 text-left"
                  data-testid={`faq-toggle-${f.id}`}
                >
                  <span className="text-base font-bold text-ink">{f.q}</span>
                  <span
                    className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                      isOpen ? "rotate-45 bg-electric text-white" : "bg-mist text-ink/50"
                    }`}
                  >
                    <Plus size={16} />
                  </span>
                </button>
                <div
                  className={`grid transition-all duration-300 ${
                    isOpen ? "mt-3 grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                  }`}
                >
                  <p className="overflow-hidden text-sm leading-relaxed text-ink/65">{f.a}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
